// Admin panel: review pending access requests (approve → active + role, deny).
// Self-contained — fetches its own data. Rendered inside the (admin-gated)
// User Management page. Renders nothing when there are no pending requests.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Check, X, Clock } from 'lucide-react';
import { apiV1 } from '@/api/client';

const ROLES = ['viewer', 'editor', 'admin'];

export default function AccessRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [roleById, setRoleById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiV1.get('/access-requests?status=pending');
      const reqs = r.data?.requests || [];
      setRows(reqs);
      setRoleById((prev) => {
        const next = { ...prev };
        reqs.forEach((q) => {
          if (!next[q.id]) next[q.id] = q.requested_role || 'viewer';
        });
        return next;
      });
    } catch (e) {
      // Non-admins / transient errors: leave the panel empty rather than noisy.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (req, decision) => {
    setBusyId(req.id);
    try {
      await apiV1.patch(`/access-requests/${req.id}`, {
        decision,
        grant_role: decision === 'approve' ? roleById[req.id] || 'viewer' : null,
        add_allowlist: true,
      });
      toast.success(
        decision === 'approve'
          ? `Approved ${req.display_name || req.email} as ${roleById[req.id] || 'viewer'}`
          : `Denied ${req.display_name || req.email}`,
      );
      await load();
    } catch (err) {
      toast.error(err?.data?.detail?.message || 'Could not update request');
    } finally {
      setBusyId(null);
    }
  };

  // Hide entirely when nothing is pending (keeps the page uncluttered).
  if (!loading && rows.length === 0) return null;

  return (
    <Card className="mb-6 border-amber-500/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-500" />
          Pending Access Requests
          {rows.length > 0 && (
            <span className="ml-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-600">
              {rows.length}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Users who signed in via Alchemy and are awaiting access approval.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Grant role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((req) => (
              <TableRow key={req.id}>
                <TableCell>
                  <div className="font-medium">{req.display_name || req.email}</div>
                  <div className="text-xs text-muted-foreground">{req.master_pers_key}</div>
                </TableCell>
                <TableCell className="text-sm">{req.requested_role || '—'}</TableCell>
                <TableCell className="max-w-[20rem] text-sm text-muted-foreground">
                  {req.reason || '—'}
                </TableCell>
                <TableCell>
                  <Select
                    value={roleById[req.id] || 'viewer'}
                    onValueChange={(v) => setRoleById((p) => ({ ...p, [req.id]: v }))}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="default"
                    className="mr-2"
                    disabled={busyId === req.id}
                    onClick={() => review(req, 'approve')}
                  >
                    <Check className="mr-1 h-4 w-4" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === req.id}
                    onClick={() => review(req, 'deny')}
                  >
                    <X className="mr-1 h-4 w-4" /> Deny
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
